import { t } from '@repo/i18n';
import { createBrowserRouter, Outlet } from 'react-router';
import { AppErrorBoundary } from './components/generic-error';
import { AdminLayout, adminLoader } from './pages/authenticated/admin/layout';
import { OrganizationsPage } from './pages/authenticated/admin/organizations';
import { CreateOrganizationPage } from './pages/authenticated/admin/organizations/create';
import { OwnersPage } from './pages/authenticated/admin/owners';
import { AuthenticatedLayout } from './pages/authenticated/layout';
import { OrganizationIdPage } from './pages/authenticated/organization/[organizationId]';
import { ClientsPage } from './pages/authenticated/organization/[organizationId]/clients';
import { ClientProfilePage } from './pages/authenticated/organization/[organizationId]/clients/profile';
import {
  OrganizationIdLayout,
  organizationIdLoader,
} from './pages/authenticated/organization/[organizationId]/layout';
import { NotificationsPage } from './pages/authenticated/organization/[organizationId]/notifications';
import { OfficersPage } from './pages/authenticated/organization/[organizationId]/officers';
import { CreateOfficerPage } from './pages/authenticated/organization/[organizationId]/officers/create';
import { OrganizationPoliciesPage } from './pages/authenticated/organization/[organizationId]/policies';
import { ReportsPage } from './pages/authenticated/organization/[organizationId]/reports';
import { ReportIdPage } from './pages/authenticated/organization/[organizationId]/reports/[reportId]';
import {
  ReportCreatePage,
  reportCreateLoader,
} from './pages/authenticated/organization/[organizationId]/reports/create';
import { ReportCreateWizardPage } from './pages/authenticated/organization/[organizationId]/reports/create/[reportId]';
import {
  ReportCreateWizardLayout,
  reportCreateWizardLoader,
} from './pages/authenticated/organization/[organizationId]/reports/create/[reportId]/layout';
import { OrganizationLayout, organizationLoader } from './pages/authenticated/organization/layout';
import { TwoFactorSetting } from './pages/authenticated/organization/settings/2fa';
import { AppearancesPage } from './pages/authenticated/organization/settings/appearances';
import { PasskeyPage } from './pages/authenticated/organization/settings/passkey';
import { PasswordSetting } from './pages/authenticated/organization/settings/password';
import { ApiTokensPage } from './pages/authenticated/organization/settings/tokens';
import { HomePage } from './pages/landing/home';
import { ActivatePage } from './pages/unauthenticated/activate';
import { CheckEmailPage } from './pages/unauthenticated/check-email';
import { ForgotPasswordPage } from './pages/unauthenticated/forgot-password';
import { UnauthenticatedLayout } from './pages/unauthenticated/layout';
import { ResetPasswordPage } from './pages/unauthenticated/reset-password';
import {
  ResetPasswordTokenPage,
  resetPasswordTokenLoader,
} from './pages/unauthenticated/reset-password-token';
import { SignIn } from './pages/unauthenticated/signin';
import { SignUp } from './pages/unauthenticated/signup';
import { UnverifiedAccount } from './pages/unauthenticated/unverified-account';
import { EmailVerificationWithoutTokenPage } from './pages/unauthenticated/verify-email';
import { VerifyEmailTokenPage } from './pages/unauthenticated/verify-email-token';
import Root, { rootLoader } from './root';

export const router = createBrowserRouter([
  {
    id: 'root',
    path: '/',
    Component: Root,
    shouldRevalidate: () => false,
    loader: rootLoader,
    ErrorBoundary: AppErrorBoundary,
    children: [
      {
        index: true,
        Component: HomePage,
      },
      {
        path: 'auth',
        Component: UnauthenticatedLayout,
        children: [
          {
            path: 'signin',
            Component: SignIn,
          },
          {
            path: 'signup',
            Component: SignUp,
          },
          {
            path: 'unverified-account',
            Component: UnverifiedAccount,
          },
          {
            path: 'verify-email',
            children: [
              {
                index: true,
                Component: EmailVerificationWithoutTokenPage,
              },
              {
                path: ':token',
                Component: VerifyEmailTokenPage,
              },
            ],
          },
          {
            path: 'activate',
            Component: ActivatePage,
          },
          {
            path: 'forgot-password',
            Component: ForgotPasswordPage,
          },
          {
            path: 'check-email',
            Component: CheckEmailPage,
          },
          {
            path: 'reset-password',
            children: [
              {
                index: true,
                Component: ResetPasswordPage,
              },
              {
                path: ':token',
                loader: resetPasswordTokenLoader,
                Component: ResetPasswordTokenPage,
              },
            ],
          },
        ],
      },
      {
        Component: AuthenticatedLayout,
        children: [
          {
            path: 'admin',
            Component: AdminLayout,
            handle: {
              get breadcrumb() {
                return t('workspace.admin');
              },
            },
            loader: adminLoader,
            children: [
              // {
              //   index: true,
              //   loader: async () => redirect('/admin/organizations'),
              // },
              {
                path: 'organizations',
                handle: {
                  get breadcrumb() {
                    return t('workspace.organizations');
                  },
                },
                Component: OrganizationsPage,
              },
              {
                path: 'organizations/create',
                handle: {
                  get breadcrumb() {
                    return t('workspace.createOrganization');
                  },
                },
                Component: CreateOrganizationPage,
              },
              {
                path: 'owners',
                handle: {
                  get breadcrumb() {
                    return t('workspace.owners');
                  },
                },
                Component: OwnersPage,
              },
            ],
          },
          {
            id: 'o',
            path: 'o',
            Component: OrganizationLayout,
            loader: organizationLoader,
            children: [
              {
                path: ':organizationId',
                loader: organizationIdLoader,
                handle: { organizationCrumb: true },
                Component: OrganizationIdLayout,
                children: [
                  {
                    index: true,
                    Component: OrganizationIdPage,
                  },
                  {
                    path: 'policies',
                    Component: OrganizationPoliciesPage,
                  },
                  {
                    path: 'officers',
                    handle: {
                      get breadcrumb() {
                        return t('workspace.officers');
                      },
                    },
                    Component: OfficersPage,
                  },
                  {
                    path: 'officers/create',
                    handle: {
                      get breadcrumb() {
                        return t('workspace.inviteOfficer');
                      },
                    },
                    Component: CreateOfficerPage,
                  },
                  {
                    path: 'clients',
                    handle: {
                      get breadcrumb() {
                        return t('workspace.clients');
                      },
                    },
                    Component: ClientsPage,
                  },
                  {
                    path: 'clients/:clientId',
                    Component: ClientProfilePage,
                    handle: {
                      get breadcrumb() {
                        return t('workspace.clientProfile');
                      },
                    },
                  },
                  {
                    path: 'bookmarks',
                    Component: ClientsPage,
                    handle: {
                      get breadcrumb() {
                        return t('workspace.bookmarks');
                      },
                    },
                  },
                  {
                    path: 'notifications',
                    Component: NotificationsPage,
                    handle: {
                      get breadcrumb() {
                        return t('workspace.notifications');
                      },
                    },
                  },
                  {
                    path: 'reports',
                    Component: Outlet,
                    handle: {
                      get breadcrumb() {
                        return t('workspace.reports');
                      },
                    },
                    children: [
                      {
                        index: true,
                        Component: ReportsPage,
                      },
                      {
                        path: 'create',
                        handle: {
                          get breadcrumb() {
                            return t('workspace.createReport');
                          },
                        },
                        loader: reportCreateLoader,
                        Component: ReportCreatePage,
                      },
                      {
                        path: 'create/:reportId',
                        id: 'report-create-wizard',
                        loader: reportCreateWizardLoader,
                        handle: {
                          get breadcrumb() {
                            return t('workspace.reportWizard');
                          },
                        },
                        Component: ReportCreateWizardLayout,
                        children: [
                          {
                            index: true,
                            Component: ReportCreateWizardPage,
                          },
                        ],
                      },
                      {
                        path: ':reportId',
                        handle: {
                          get breadcrumb() {
                            return t('workspace.reportPage');
                          },
                        },
                        Component: ReportIdPage,
                      },
                    ],
                  },
                  {
                    path: 'settings',
                    Component: Outlet,
                    handle: {
                      get breadcrumb() {
                        return t('workspace.settings');
                      },
                    },
                    children: [
                      {
                        path: 'tokens',
                        handle: {
                          get breadcrumb() {
                            return t('workspace.apiTokens');
                          },
                        },
                        Component: ApiTokensPage,
                      },
                      {
                        path: 'password',
                        handle: {
                          get breadcrumb() {
                            return t('auth.resetPassword');
                          },
                        },
                        Component: PasswordSetting,
                      },
                      {
                        path: '2fa',
                        handle: {
                          get breadcrumb() {
                            return t('workspace.twoFactorAuthentication');
                          },
                        },
                        Component: TwoFactorSetting,
                      },
                      {
                        path: 'passkeys',
                        handle: {
                          get breadcrumb() {
                            return t('workspace.passkey');
                          },
                        },
                        Component: PasskeyPage,
                      },
                      {
                        path: 'appearances',
                        handle: {
                          get breadcrumb() {
                            return t('workspace.appearances');
                          },
                        },
                        Component: AppearancesPage,
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
]);
